import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as Actions from '../actions/budget-vs-actual.actions';
import {BudgetOptionValue, BudgetVsActualData, SortOrderValue, Schedule} from '../models/budget-vs-actual-data.model';
import * as actions from "@feature/pma-document-centre/actions/pma-document-centre.actions";
import * as widgetConfigActions from "@dashboard-widget/actions/dashboard-widget-config.actions";

export class BudgetVsActualState {
  id: number;
  loading?: boolean;
  data?: BudgetVsActualData[];
  scheduleRefs?: number[];
  currencyCode?: string;
  propertyName?: string;
  propertyRef?: number;
  year?: string;
  periods?: (string | number)[];
  columns?: string[];
  loadingColumns?: boolean;
  lowBudgetThreshold: number;
  overBudgetThreshold: number;
  selectedSortOrder?: SortOrderValue;
  selectedBudgetForChart?: BudgetOptionValue;
  unitRef?: number;
  includeCommitments?: boolean;
  includeCommitmentsSystemDefault?: boolean;
}

export const adapter: EntityAdapter<BudgetVsActualState> = createEntityAdapter<BudgetVsActualState>();

const INITIAL_INSTANCE_STATE: BudgetVsActualState = {
  id: undefined,
  loading: false,
  data: undefined,
  scheduleRefs: undefined,
  currencyCode: undefined,
  propertyName: undefined,
  propertyRef: undefined,
  year: undefined,
  periods: undefined,
  columns: undefined,
  loadingColumns: undefined,
  lowBudgetThreshold: undefined,
  overBudgetThreshold: undefined,
  selectedSortOrder: undefined,
  selectedBudgetForChart: undefined,
  unitRef: undefined,
  includeCommitments: true,
  includeCommitmentsSystemDefault: true
};

export interface State extends EntityState<BudgetVsActualState>{}

const defaultBudgetSelection = {BudgetField: "Budget1", VarianceField: "VarianceAmount1"};

export const initialState: State = adapter.getInitialState({
  id: undefined,
  loading: false,
  data: undefined,
  scheduleRefs: undefined,
  scheduleLabels: undefined,
  currencyCode: undefined,
  columns: undefined,
  loadingColumns: false,
  lowBudgetThreshold: 10,
  overBudgetThreshold: 0,
  propertyName: undefined,
  propertyRef: undefined,
  year: undefined,
  selectedSortOrder: "BudgetOrder",
  periods: [],
  selectedBudgetForChart: defaultBudgetSelection,
  includeCommitments: true,
  includeCommitmentsSystemDefault: true
});

const budgetVsActualReducer = createReducer(
  initialState,
  on(Actions.BudgetDataLoad, (state, { widgetId }) => {
    const instance: BudgetVsActualState = {
        id: widgetId,
        loading: true,
        data: null,
        currencyCode: undefined,
        periods: [],
      lowBudgetThreshold: 10,
      overBudgetThreshold: 0,
    };
    return adapter.upsertOne(instance, state);
  }),
  on(Actions.BudgetDataLoadComplete, (
    state,
    { widgetId, data, currencyCode, propertyName, propertyRef,
      year, unitRef }) => {
    return adapter.updateOne(
      { id: widgetId, changes: {
          loading: false,
          data: data,
          currencyCode: currencyCode,
          year: year,
          propertyRef: propertyRef,
          propertyName: propertyName,
          selectedBudgetForChart: defaultBudgetSelection,
          unitRef: unitRef} },
      state
    );
  }),
  on(Actions.getBvAWidgetConfigComplete,(state, { widgetId, config }) => {
    return adapter.updateOne(
      { id: widgetId, changes: {
        includeCommitments: config.IncludeCommitments
      } },
      state
    );
  }),
  on(Actions.BudgetDataLoadError, (state, { widgetId }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { loading: false} },
      state
    );
  }),

  on(Actions.SelectedColumnsLoad, (state, { widgetId }) => {
    return adapter.addOne(
      { id: widgetId, loadingColumns: true, lowBudgetThreshold: 10, overBudgetThreshold: 0 },
      state
    );
  }),

  on(Actions.SelectedColumnsLoadComplete, (state, { widgetId, columns }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { columns: columns, loadingColumns: false } },
      state
    );
  }),

  on(Actions.SelectedColumnsLoadError, (state, { widgetId }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { loadingColumns: false} },
      state
    );
  }),

  on(Actions.ScheduleChange, (state, { widgetId, scheduleRefs }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { scheduleRefs: scheduleRefs } },
      state
    );
  }),

on(Actions.SortOrderChange, (state, { widgetId, selectedSortOrder }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { selectedSortOrder: selectedSortOrder  } },
      state
    );
  }),
  on(Actions.ColumnsChange, (state, { widgetId, columns }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { columns: columns } },
      state
    );
  }),

 on(Actions.PeriodChanged, (state, { widgetId, period }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { periods: period } },
      state
    );
  }),

  on(Actions.SelectedBudgetChange, (state, { widgetId, selectedBudget }) => {
    return adapter.updateOne(
      { id: widgetId, changes: { selectedBudgetForChart: selectedBudget } },
      state
    );
  }),
  on(Actions.Clear, (state, { widgetId }) => {

    return adapter.updateOne({
      id: widgetId,
      changes: {
        loading: undefined,
        scheduleRefs: undefined,
        data: undefined,
        columns: undefined,
        currencyCode: undefined,
        loadingColumns: undefined,
        lowBudgetThreshold: undefined,
        overBudgetThreshold: undefined,
        periods:[],
        selectedSortOrder: undefined,
        includeCommitments: undefined,
        includeCommitmentsSystemDefault: undefined
      }
    }, state);
  }),
  on(actions.apiLoad, (state, { widgetId }) => {
    let item: BudgetVsActualState = state.entities[widgetId] || INITIAL_INSTANCE_STATE;
    item = { ...item, ...{ id: widgetId, loading: true } };
    return adapter.upsertOne(item, state);
  })
);

export function reducer(state: State | undefined, action: Action) {
  return budgetVsActualReducer(state, action);
}
